"use client";

import { useEffect, useState } from "react";

export type DeviceType = "mobile" | "tablet" | "desktop";

export interface DeviceInfo {
  deviceType: DeviceType;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  screenWidth: number;
  screenHeight: number;
  orientation: "portrait" | "landscape";
  userAgent: {
    isIOS: boolean;
    isAndroid: boolean;
    isSafari: boolean;
    isChrome: boolean;
    isFirefox: boolean;
  };
}

interface DeviceBreakpoints {
  mobile: number;
  tablet: number;
}

const defaultBreakpoints: DeviceBreakpoints = {
  mobile: 768,
  tablet: 1024,
};

/**
 * Advanced hook for comprehensive device detection
 * @param breakpoints - Custom breakpoints for mobile and tablet
 * @returns Detailed device information
 */
export function useDeviceDetection(
  breakpoints: DeviceBreakpoints = defaultBreakpoints
): DeviceInfo {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    deviceType: "desktop",
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    screenWidth: 0,
    screenHeight: 0,
    orientation: "landscape",
    userAgent: {
      isIOS: false,
      isAndroid: false,
      isSafari: false,
      isChrome: false,
      isFirefox: false,
    },
  });

  useEffect(() => {
    const detectDevice = () => {
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const orientation = screenWidth > screenHeight ? "landscape" : "portrait";

      // Determine device type based on screen size
      let deviceType: DeviceType = "desktop";
      if (screenWidth < breakpoints.mobile) {
        deviceType = "mobile";
      } else if (screenWidth < breakpoints.tablet) {
        deviceType = "tablet";
      }

      // User agent detection
      const userAgent = navigator.userAgent;
      const userAgentInfo = {
        isIOS: /iPad|iPhone|iPod/.test(userAgent),
        isAndroid: /Android/.test(userAgent),
        isSafari: /Safari/.test(userAgent) && !/Chrome/.test(userAgent),
        isChrome: /Chrome/.test(userAgent),
        isFirefox: /Firefox/.test(userAgent),
      };

      // Override device type for tablets detected by user agent
      if (userAgentInfo.isIOS && /iPad/.test(userAgent)) {
        deviceType = "tablet";
      }

      setDeviceInfo({
        deviceType,
        isMobile: deviceType === "mobile" || deviceType === "tablet", // Both mobile and tablet are considered mobile
        isTablet: deviceType === "tablet",
        isDesktop: deviceType === "desktop",
        screenWidth,
        screenHeight,
        orientation,
        userAgent: userAgentInfo,
      });
    };

    // Initial detection
    detectDevice();

    // Listen for resize and orientation changes
    window.addEventListener("resize", detectDevice);
    window.addEventListener("orientationchange", detectDevice);

    return () => {
      window.removeEventListener("resize", detectDevice);
      window.removeEventListener("orientationchange", detectDevice);
    };
  }, [breakpoints.mobile, breakpoints.tablet]);

  return deviceInfo;
}

/**
 * Simple hook that returns only the device type
 * @param breakpoints - Custom breakpoints for mobile and tablet
 * @returns Device type string
 */
export function useDeviceType(breakpoints: DeviceBreakpoints = defaultBreakpoints): DeviceType {
  const { deviceType } = useDeviceDetection(breakpoints);
  return deviceType;
}
